import React, { useState, useEffect, useMemo } from 'react';
import { AZURE } from '../../services/api';
import { COLORS } from '../../components/governance/governanceTheme';
import { BASELINE_SCENARIO_KEY } from '../../constants/governance';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const PLANNER_LABELS = {
  pt: {
    title: 'Governance Canvas',
    subtitle: 'Drag & Drop para gerenciar tasks por trilha (Frontend, Backend, Arquitetura, Researcher, Financeiro)',
    reload: '🔄 Recarregar',
    snapshot: 'Fotografia de hoje',
    snapshotDesc: 'Estado atual do plano usado pelo Planner (snapshot consolidado).',
    environment: 'Ambiente', generatedAt: 'Gerado em', plan: 'Plano', totalItems: 'Total de items',
    trackDist: 'Tasks por trilha',
    trackDistDesc: 'Visão rápida de quantas tasks existem em cada trilha. Use o canvas abaixo para mover entre Now/Next/Later/Done/Blocked.',
    pctTasks: (p) => `${p}% das tasks`,
    noTrackDist: 'Nenhuma distribuição de trilhas disponível ainda.',
    impactTitle: '📊 Impacto Financeiro dos Projetos',
    impactDesc: 'Custo e receita estimados por projeto. Campos em branco = sem dado real cadastrado.',
    projects: 'projetos', totalEffortLabel: 'esforço total',
    estimatedCostLabel: 'custo estimado', estimatedRevenueLabel: 'receita estimada',
    thProject: 'Projeto', thTrack: 'Trilha', thArea: 'Área', thEffort: 'Esforço',
    thImpact: 'Impact', thCostEst: 'Custo Est.', thRevenueEst: 'Receita Est.',
    thProduct: 'Produto', thStatus: 'Status',
    noItems: 'Nenhum item encontrado no canvas.',
    showing: (total) => `Mostrando 30 de ${total} itens.`,
    pending: 'pendente',
    empty: 'Vazio',
    noCanvasTitle: 'Nenhum Canvas disponível',
    noCanvasDesc: 'Nenhum plano foi gerado ainda ou não há items com track definido.',
  },
  en: {
    title: 'Governance Canvas',
    subtitle: 'Drag & Drop to manage tasks by track (Frontend, Backend, Architecture, Researcher, Financial)',
    reload: '🔄 Reload',
    snapshot: "Today's snapshot",
    snapshotDesc: 'Current state of the plan used by the Planner (consolidated snapshot).',
    environment: 'Environment', generatedAt: 'Generated at', plan: 'Plan', totalItems: 'Total items',
    trackDist: 'Tasks by track',
    trackDistDesc: 'Quick view of how many tasks exist in each track. Use the canvas below to move between Now/Next/Later/Done/Blocked.',
    pctTasks: (p) => `${p}% of tasks`,
    noTrackDist: 'No track distribution available yet.',
    impactTitle: '📊 Financial Impact of Projects',
    impactDesc: 'Estimated cost and revenue per project. Blank fields = no real data registered.',
    projects: 'projects', totalEffortLabel: 'total effort',
    estimatedCostLabel: 'estimated cost', estimatedRevenueLabel: 'estimated revenue',
    thProject: 'Project', thTrack: 'Track', thArea: 'Area', thEffort: 'Effort',
    thImpact: 'Impact', thCostEst: 'Est. Cost', thRevenueEst: 'Est. Revenue',
    thProduct: 'Product', thStatus: 'Status',
    noItems: 'No items found in the canvas.',
    showing: (total) => `Showing 30 of ${total} items.`,
    pending: 'pending',
    empty: 'Empty',
    noCanvasTitle: 'No Canvas available',
    noCanvasDesc: 'No plan has been generated yet or there are no items with track defined.',
  },
};

// ─── Impacto Financeiro Panel ────────────────────────────────────────────────
const PRODUCT_MAP = {
  swimvision: 'SwimVision',
  trackvision: 'TrackVision',
  reakt: 'Reakt',
  vukz_assist: 'VUKZ Assist',
  governanca: 'Governança',
  financeiro: 'Financeiro',
  investidores: 'Investidores',
  rh: 'RH',
  comercial: 'Comercial',
  marketing: 'Marketing',
};

const AREA_PRODUCT_MAP = {
  ai: 'swimvision',
  backend: null,
  frontend: null,
  financeiro: 'financeiro',
  pesquisa: null,
};

const FinancialImpactPanel = ({ canvas, t }) => {
  const items = useMemo(() => {
    if (!canvas) return [];
    const all = [];
    Object.entries(canvas).forEach(([trackName, trackData]) => {
      const cols = trackData.columns || {};
      Object.values(cols).forEach(colItems => {
        if (Array.isArray(colItems)) {
          colItems.forEach(item => all.push({ ...item, _track: trackName }));
        }
      });
      if (Array.isArray(trackData.items)) {
        trackData.items.forEach(item => all.push({ ...item, _track: trackName }));
      }
    });
    return all;
  }, [canvas]);

  const byArea = useMemo(() => {
    const groups = {};
    items.forEach(item => {
      const area = item.area || item._track || 'outros';
      if (!groups[area]) groups[area] = [];
      groups[area].push(item);
    });
    return groups;
  }, [items]);

  const totalEffort = items.reduce((s, i) => s + (i.effort_points || 0), 0);
  const totalWithCost = items.filter(i => i.estimated_cost != null).length;
  const totalCost = items.reduce((s, i) => s + (i.estimated_cost || 0), 0);
  const totalRevenue = items.reduce((s, i) => s + (i.expected_revenue || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: '#f0fdf4' }}>
        <div>
          <h2 className="font-bold" style={{ color: BRAND_GREEN }}>{t.impactTitle}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{t.impactDesc}</p>
        </div>
        <div className="flex gap-4 text-xs text-right">
          <div>
            <div className="font-bold text-gray-700">{items.length}</div>
            <div className="text-gray-400">{t.projects}</div>
          </div>
          <div>
            <div className="font-bold text-gray-700">{totalEffort}pt</div>
            <div className="text-gray-400">{t.totalEffortLabel}</div>
          </div>
          {totalCost > 0 && (
            <div>
              <div className="font-bold" style={{ color: '#dc2626' }}>R$ {(totalCost / 1000).toFixed(0)}K</div>
              <div className="text-gray-400">{t.estimatedCostLabel}</div>
            </div>
          )}
          {totalRevenue > 0 && (
            <div>
              <div className="font-bold" style={{ color: BRAND_GREEN }}>R$ {(totalRevenue / 1000).toFixed(0)}K</div>
              <div className="text-gray-400">{t.estimatedRevenueLabel}</div>
            </div>
          )}
        </div>
      </div>

      {totalWithCost === 0 && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
          <p className="text-xs text-amber-700">
            ⚠️ Nenhum projeto possui <code>estimated_cost</code> ou <code>expected_revenue</code> cadastrado no banco.
            Esses campos devem ser adicionados via backend em <code>planner/items</code>. Os projetos abaixo são exibidos
            com esforço (pontos) mas sem impacto financeiro real.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2 text-left text-gray-500">{t.thProject}</th>
              <th className="px-4 py-2 text-left text-gray-500">{t.thTrack}</th>
              <th className="px-4 py-2 text-left text-gray-500">{t.thArea}</th>
              <th className="px-4 py-2 text-center text-gray-500">{t.thEffort}</th>
              <th className="px-4 py-2 text-center text-gray-500">{t.thImpact}</th>
              <th className="px-4 py-2 text-right text-gray-500">{t.thCostEst}</th>
              <th className="px-4 py-2 text-right text-gray-500">{t.thRevenueEst}</th>
              <th className="px-4 py-2 text-left text-gray-500">{t.thProduct}</th>
              <th className="px-4 py-2 text-left text-gray-500">{t.thStatus}</th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 30).map((item, i) => {
              const productKey = item.product || AREA_PRODUCT_MAP[item.area] || null;
              const productLabel = productKey ? (PRODUCT_MAP[productKey] || productKey) : null;
              return (
                <tr key={item.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-2 font-medium text-gray-800 max-w-[200px] truncate">{item.title}</td>
                  <td className="px-4 py-2 text-gray-500">{item._track}</td>
                  <td className="px-4 py-2">
                    {item.area && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        item.area === 'ai' ? 'bg-purple-100 text-purple-700' :
                        item.area === 'backend' ? 'bg-blue-100 text-blue-700' :
                        item.area === 'frontend' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{item.area}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center text-gray-600">{item.effort_points ?? '—'}</td>
                  <td className="px-4 py-2 text-center">{item.impact_score != null ? item.impact_score.toFixed(1) : '—'}</td>
                  <td className="px-4 py-2 text-right">
                    {item.estimated_cost != null
                      ? <span style={{ color: '#dc2626' }}>R$ {(item.estimated_cost / 1000).toFixed(0)}K</span>
                      : <span className="text-gray-300">{t.pending}</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {item.expected_revenue != null
                      ? <span style={{ color: BRAND_GREEN }}>R$ {(item.expected_revenue / 1000).toFixed(0)}K</span>
                      : <span className="text-gray-300">{t.pending}</span>}
                  </td>
                  <td className="px-4 py-2">
                    {productLabel
                      ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#d1fae5', color: BRAND_GREEN }}>{productLabel}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      item.canvas_status === 'done' ? 'bg-green-100 text-green-700' :
                      item.canvas_status === 'blocked' ? 'bg-red-100 text-red-700' :
                      item.canvas_status === 'now' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{item.canvas_status || 'now'}</span>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                  {t.noItems}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {items.length > 30 && (
          <p className="text-xs text-gray-400 px-4 py-2">{t.showing(items.length)}</p>
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-400">
          Para adicionar <code>estimated_cost</code>, <code>expected_revenue</code> e <code>product</code> aos itens,
          adicione esses campos nas tabelas do planner via migration SQL e exponha via <code>PUT /governance/planner/items/:id</code>.
          Cenário padrão de referência: <strong>{BASELINE_SCENARIO_KEY}</strong>.
        </p>
      </div>
    </div>
  );
};

// Canvas Column Component (Droppable)
const CanvasColumn = ({ title, items, status, trackName, trackColor, onDropItem, t }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    const data = e.dataTransfer.getData("application/json");
    if (data) {
      try {
        const { itemId, sourceTrack, sourceStatus } = JSON.parse(data);
        // Only trigger if changed
        if (sourceTrack !== trackName || sourceStatus !== status) {
          onDropItem(itemId, trackName, status);
        }
      } catch (err) {
        console.error("Drop error", err);
      }
    }
  };

  return (
    <div 
      className={`flex-1 min-w-[280px] transition-colors ${isOver ? 'bg-blue-50' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div 
        className="rounded-t-lg px-4 py-3 font-bold text-white shadow-sm"
        style={{ backgroundColor: trackColor }}
      >
        <div className="flex items-center justify-between">
          <span>{title}</span>
          <span className="bg-white bg-opacity-20 px-2 py-0.5 rounded-full text-sm">
            {items.length}
          </span>
        </div>
      </div>
      
      <div 
        className={`bg-gray-50 border-2 border-t-0 rounded-b-lg p-3 min-h-[400px] space-y-3 transition-colors ${
          isOver ? 'border-blue-300 bg-blue-50' : ''
        }`} 
        style={{ borderColor: trackColor }}
      >
        {items.length === 0 ? (
          <div className="text-center text-gray-400 py-8 pointer-events-none">
            <div className="text-4xl mb-2">📋</div>
            <div className="text-sm">{t ? t.empty : 'Empty'}</div>
          </div>
        ) : (
          items.map((item) => (
            <DraggableCard 
              key={item.id} 
              item={item} 
              trackName={trackName} 
              trackColor={trackColor}
              status={status}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Draggable Card Component
const DraggableCard = ({ item, trackName, trackColor, status }) => {
  const handleDragStart = (e) => {
    e.dataTransfer.setData("application/json", JSON.stringify({
      itemId: item.id,
      sourceTrack: trackName,
      sourceStatus: status
    }));
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="bg-white rounded-lg border-2 p-3 shadow-sm hover:shadow-md transition-all cursor-move active:opacity-50"
      style={{ borderColor: trackColor }}
    >
      <div className="font-semibold text-gray-900 mb-2 text-sm">
        {item.title}
      </div>
      
      <div className="flex items-center gap-2 text-xs">
        <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-600 font-medium">
          {item.effort_points}pt
        </span>
        <span className={`px-2 py-0.5 rounded font-medium ${
          (item.impact_score || 0) > 4 ? 'bg-red-100 text-red-700' :
          (item.impact_score || 0) > 2 ? 'bg-blue-50 text-blue-700' :
          'bg-gray-100 text-gray-600'
        }`}>
          Impact: {item.impact_score?.toFixed(1)}
        </span>
      </div>
      
      {item.area && (
        <div className="mt-2">
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
            item.area === 'ai' ? 'bg-purple-100 text-purple-700' :
            item.area === 'backend' ? 'bg-blue-100 text-blue-700' :
            item.area === 'frontend' ? 'bg-green-100 text-green-700' :
            item.area === 'infra' ? 'bg-orange-100 text-orange-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {item.area}
          </span>
        </div>
      )}
      
      {item.dependencies && item.dependencies.length > 0 && (
        <div className="mt-2 text-xs text-gray-500">
          🔗 {item.dependencies.length} deps
        </div>
      )}
    </div>
  );
};

// Track Canvas Component (5 colunas: now/next/later/done/blocked)
const TrackCanvas = ({ trackName, trackData, trackColor, onDropItem, t }) => {
  // Normalize items list from potential backend formats
  const items = Array.isArray(trackData.columns) ? [] : (trackData.items || []);
  
  // Use columns object if present from backend, otherwise filter items list
  let columns = trackData.columns || {};
  
  if (!trackData.columns) {
     columns = {
      now: items.filter(i => (i.canvas_status || 'now') === 'now'),
      next: items.filter(i => i.canvas_status === 'next'),
      later: items.filter(i => i.canvas_status === 'later'),
      done: items.filter(i => i.canvas_status === 'done'),
      blocked: items.filter(i => i.canvas_status === 'blocked'),
    };
  }

  const total_effort = items.length > 0 
    ? items.reduce((sum, i) => sum + (i.effort_points || 0), 0)
    : (trackData.total_effort || 0);

  const total_count = items.length > 0 ? items.length : (trackData.total_items || 0);

  return (
    <div className="mb-8">
      <div className="mb-4">
        <div className="flex items-baseline gap-3">
          <h3 className="text-2xl font-extrabold" style={{ color: trackColor }}>
            {trackName}
          </h3>
          <span className="text-sm text-gray-500">
            {total_count} items • {total_effort} pontos
          </span>
        </div>
      </div>
      
      <div className="flex gap-4 overflow-x-auto pb-4">
        {['now', 'next', 'later', 'done', 'blocked'].map(status => (
          <CanvasColumn 
            key={status}
            title={status.charAt(0).toUpperCase() + status.slice(1)} 
            items={columns[status] || []} 
            status={status}
            trackName={trackName}
            trackColor={trackColor}
            onDropItem={onDropItem}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

// Main Component
const GovernancePlannerPage = () => {
  const { lang } = useGovernanceLang();
  const t = PLANNER_LABELS[lang] || PLANNER_LABELS.pt;
  const [canvas, setCanvas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotMeta, setSnapshotMeta] = useState(null);

  useEffect(() => {
    loadCanvas();
  }, []);

  const loadCanvas = async () => {
    setLoading(true);
    try {
      const { data } = await AZURE.get('/governance/planner/snapshot');
      if (data.success && data.data) {
        const payload = data.data;
        if (payload.canvas) {
          setCanvas(payload.canvas);
        }
        if (payload.snapshot) {
          setSnapshot(payload.snapshot);
        }
        setSnapshotMeta({
          environment: payload.environment || 'production',
          generated_at: payload.generated_at || null,
          snapshot_id: payload.snapshot_id || null
        });
      }
    } catch (error) {
      console.error('Error loading canvas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDropItem = async (itemId, targetTrack, targetStatus) => {
    console.log(`Moving item ${itemId} to ${targetTrack} / ${targetStatus}`);
    
    // 1. Optimistic Update
    setCanvas(prevCanvas => {
      const newCanvas = { ...prevCanvas };
      
      // We need to find the item ANYWHERE in the canvas and move it
      let foundItem = null;
      let sourceTrackName = null;
      let sourceStatus = null;
      
      // Search in all tracks
      Object.entries(newCanvas).forEach(([tName, tData]) => {
        // Handle both structure types (legacy columns object vs flat items array)
        if (tData.columns) {
           Object.entries(tData.columns).forEach(([colStatus, items]) => {
             const idx = items.findIndex(i => i.id === itemId);
             if (idx !== -1) {
               foundItem = { ...items[idx] };
               sourceTrackName = tName;
               sourceStatus = colStatus;
               // Remove from source
               tData.columns[colStatus].splice(idx, 1);
               tData.total_items--;
               tData.total_effort -= (foundItem.effort_points || 0);
             }
           });
        }
      });
      
      if (foundItem) {
        // Update item properties
        foundItem.canvas_status = targetStatus;
        foundItem.track = targetTrack;
        
        // Add to target
        if (!newCanvas[targetTrack].columns) {
            newCanvas[targetTrack].columns = { now: [], next: [], later: [], done: [], blocked: [] };
        }
        if (!newCanvas[targetTrack].columns[targetStatus]) {
            newCanvas[targetTrack].columns[targetStatus] = [];
        }
        
        newCanvas[targetTrack].columns[targetStatus].push(foundItem);
        newCanvas[targetTrack].total_items++;
        newCanvas[targetTrack].total_effort += (foundItem.effort_points || 0);
      }
      
      return newCanvas;
    });

    // 2. API Call
    try {
      await AZURE.put(`/governance/planner/items/${itemId}/status`, {
        canvas_status: targetStatus,
        track: targetTrack
      });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to move item. Reloading canvas.');
      loadCanvas(); // Revert on error
    }
  };

  const trackColors = {
    'Frontend': COLORS.primary,
    'Backend': '#2563eb',
    'Arquitetura': '#7c3aed',
    'Researcher': '#0891b2',
    'Financeiro': COLORS.gold
  };

  // Deriva uma fotografia mínima a partir do canvas caso o backend não envie snapshot
  const effectiveSnapshot = snapshot || (canvas ? (() => {
    const tracks = Object.keys(canvas || {});
    const tracks_distribution = {};
    let total_items = 0;

    tracks.forEach(trackName => {
      const t = canvas[trackName];
      if (!t) return;
      // Suporta tanto formato "columns" quanto lista simples
      let count = 0;
      if (t.columns) {
        count = Object.values(t.columns).reduce(
          (sum, colItems) => sum + (Array.isArray(colItems) ? colItems.length : 0),
          0
        );
      } else if (Array.isArray(t)) {
        count = t.length;
      } else if (Array.isArray(t.items)) {
        count = t.items.length;
      }
      tracks_distribution[trackName] = count;
      total_items += count;
    });

    return {
      plan_id: null,
      items: [],
      total_items,
      tracks_distribution
    };
  })() : null);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-10 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!canvas) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">
              {t.noCanvasTitle}
            </h3>
            <p className="text-gray-500">
              {t.noCanvasDesc}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-end">
          <div className="flex items-center gap-3">
            <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
            <h1 className="text-gray-900 text-3xl font-extrabold mb-2">
              {t.title}
            </h1>
            <p className="text-gray-600">
              {t.subtitle}
            </p>
            </div>
          </div>
          <button
            onClick={loadCanvas}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-600"
          >
            {t.reload}
          </button>
        </div>

        {/* Fotografia de hoje + resumo de trilhas */}
        {effectiveSnapshot && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Fotografia de hoje */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:col-span-1">
              <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>
                {t.snapshot}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t.snapshotDesc}
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t.environment}</span>
                  <span className="font-medium">{snapshotMeta?.environment || 'production'}</span>
                </div>
                {snapshotMeta?.generated_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.generatedAt}</span>
                    <span className="font-medium">
                      {new Date(snapshotMeta.generated_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                )}
                {effectiveSnapshot.plan_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.plan}</span>
                    <span className="font-medium">#{effectiveSnapshot.plan_id}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">{t.totalItems}</span>
                  <span className="font-bold" style={{ color: COLORS.primary }}>
                    {effectiveSnapshot.total_items ?? 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Distribuição por trilha (tasks lado a lado) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:col-span-2">
              <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.primary }}>
                {t.trackDist}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t.trackDistDesc}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(effectiveSnapshot.tracks_distribution || {}).map(([trackName, count]) => (
                  <div
                    key={trackName}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-100 flex flex-col gap-1"
                  >
                    <span className="text-xs text-gray-500">{trackName}</span>
                    <span className="text-lg font-bold" style={{ color: COLORS.primary }}>
                      {count}
                    </span>
                    {effectiveSnapshot.total_items > 0 && (
                      <span className="text-xs text-gray-500">
                        {t.pctTasks(Math.round((count / effectiveSnapshot.total_items) * 100))}
                      </span>
                    )}
                  </div>
                ))}
                {Object.keys(effectiveSnapshot.tracks_distribution || {}).length === 0 && (
                  <div className="text-sm text-gray-500">
                    {t.noTrackDist}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Impacto Financeiro dos Projetos */}
        <FinancialImpactPanel canvas={canvas} t={t} />

        {/* Canvas por Track */}
        <div className="space-y-8 pb-32">
          {Object.entries(canvas).map(([trackName, trackData]) => (
            <TrackCanvas 
              key={trackName}
              trackName={trackName}
              trackData={trackData}
              trackColor={trackColors[trackName] || COLORS.primary}
              onDropItem={handleDropItem}
              t={t}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GovernancePlannerPage;
